import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { GridStatus } from '../../types/energyData';
import { EnergyDataService } from '../../services/energyDataService';

interface GridDetailViewProps {
  data: GridStatus;
  energyDataService: EnergyDataService;
}

export const GridDetailView: React.FC<GridDetailViewProps> = ({ data, energyDataService }) => {
  const { t } = useTranslation();
  
  // Get REAL time series data from JSON files - NO FAKE DATA
  const gridTimeSeriesData = energyDataService.getGridTimeSeriesData();
  
  // Use real data if available, otherwise show current values only
  const hasGridData = gridTimeSeriesData.importData.length > 0;
  
  const timeData = hasGridData ? gridTimeSeriesData.timeLabels : ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
  
  // Use REAL data from JSON files - NO RANDOM VARIATIONS
  const importPowerData = hasGridData ? gridTimeSeriesData.importData : [data.importPower, data.importPower, data.importPower, data.importPower, data.importPower, data.importPower, data.importPower];
  const exportPowerData = hasGridData ? gridTimeSeriesData.exportData : [data.exportPower, data.exportPower, data.exportPower, data.exportPower, data.exportPower, data.exportPower, data.exportPower];
  
  // Energy data should be based on actual power values
  const importEnergyData = importPowerData.map((power, i) => data.importEnergy + (power * i * 0.25));
  const exportEnergyData = exportPowerData.map((power, i) => data.exportEnergy + (power * i * 0.25));

  const powerFlowChartOption = {
    title: {
      text: t('charts.gridPowerFlowOverTime'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          const value = param.value;
          const type = value > 0 ? t('grid.importing') : value < 0 ? t('grid.exporting') : t('grid.neutral');
          result += `${param.seriesName}: ${Math.abs(value).toFixed(1)} kW (${type})<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [t('legends.importPower'), t('legends.exportPower')],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kW'
      }
    },
        series: [
          {
            name: t('legends.importPower'),
        data: importPowerData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#EF4444' },
        areaStyle: { color: 'rgba(239, 68, 68, 0.2)' }
      },
          {
            name: t('legends.exportPower'),
        data: exportPowerData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#10B981' },
        areaStyle: { color: 'rgba(16, 185, 129, 0.2)' }
      }
    ]
  };

  const energyChartOption = {
    title: {
      text: t('charts.totalEnergyImportExport'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          result += `${param.seriesName}: ${param.value} kWh<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [t('legends.importEnergy'), t('legends.exportEnergy')],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kWh'
      }
    },
        series: [
          {
            name: t('legends.importEnergy'),
        data: importEnergyData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#EF4444' },
        areaStyle: { color: 'rgba(239, 68, 68, 0.2)' }
      },
          {
            name: t('legends.exportEnergy'),
        data: exportEnergyData,
        type: 'line',
        smooth: true,
        lineStyle: { color: '#10B981' },
        areaStyle: { color: 'rgba(16, 185, 129, 0.2)' }
      }
    ]
  };

  const tariffChartOption = {
    title: {
      text: t('charts.energyTariffs'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        return `${params[0].axisValue}: ${params[0].value} ct/kWh`;
      }
    },
    xAxis: {
      type: 'category',
      data: [t('grid.importTariff'), t('grid.exportTariff')]
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} ct/kWh'
      }
    },
    series: [{
      data: [
        { value: data.importTariff, itemStyle: { color: '#EF4444' } },
        { value: data.exportTariff, itemStyle: { color: '#10B981' } }
      ],
      type: 'bar'
    }]
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">⚡ {t('grid.title')}</h2>
            <p className="text-gray-600">{t('grid.subtitle')}</p>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${data.netPower > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {data.netPower > 0 ? '+' : ''}{data.netPower.toFixed(1)} kW
            </div>
            <div className="text-sm text-gray-500">{t('grid.netPowerFlow')}</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={powerFlowChartOption} style={{ height: '300px' }} />
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={energyChartOption} style={{ height: '300px' }} />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <ReactECharts option={tariffChartOption} style={{ height: '300px' }} />
      </div>

      {/* Grid Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 {t('grid.currentGridStatus')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('grid.importPower')}</span>
              <span className="font-semibold text-red-600">{data.importPower.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('grid.exportPower')}</span>
              <span className="font-semibold text-green-600">{data.exportPower.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('grid.netPowerFlow')}</span>
              <span className={`font-semibold ${data.netPower > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {data.netPower > 0 ? '+' : ''}{data.netPower.toFixed(1)} kW
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('grid.flowDirection')}</span>
              <span className={`font-semibold ${data.netPower > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {data.netPower > 0 ? t('grid.importing') : data.netPower < 0 ? t('grid.exporting') : t('grid.neutral')}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('grid.gridStatus')}</span>
              <span className="font-semibold text-green-600">✓ {t('grid.connected')}</span>
            </div>
          </div>
        </div>

        {/* Energy Trading */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">💰 {t('grid.energyTrading')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('grid.importEnergyToday')}</span>
              <span className="font-semibold">{data.importEnergy.toFixed(1)} kWh</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('grid.exportEnergyToday')}</span>
              <span className="font-semibold">{data.exportEnergy.toFixed(1)} kWh</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('grid.importTariff')}</span>
              <span className="font-semibold">{data.importTariff.toFixed(2)} ct/kWh</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('grid.exportTariff')}</span>
              <span className="font-semibold">{data.exportTariff.toFixed(2)} ct/kWh</span>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">💵 {t('grid.financialSummary')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">€{(data.importEnergy * data.importTariff / 100).toFixed(2)}</div>
            <div className="text-sm text-gray-600">{t('grid.importCost')}</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">€{(data.exportEnergy * data.exportTariff / 100).toFixed(2)}</div>
            <div className="text-sm text-gray-600">{t('grid.exportRevenue')}</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className={`text-2xl font-bold ${(data.exportEnergy * data.exportTariff / 100) - (data.importEnergy * data.importTariff / 100) > 0 ? 'text-green-600' : 'text-red-600'}`}>
              €{((data.exportEnergy * data.exportTariff / 100) - (data.importEnergy * data.importTariff / 100)).toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">{t('grid.netResult')}</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{((data.exportEnergy / (data.importEnergy + data.exportEnergy)) * 100).toFixed(1)}%</div>
            <div className="text-sm text-gray-600">{t('grid.selfSufficiency')}</div>
          </div>
        </div>
      </div>

      {/* Grid Quality */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🔌 {t('grid.gridQualityStability')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">{t('grid.powerQuality')}</h4>
            <div className="space-y-2">
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">{t('grid.gridEvents')}</h4>
            <div className="space-y-2">
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
